import React, { useMemo, useState } from 'react';
import styles from './TipTypeSelection.module.scss';
import classNames from 'classnames';
import { Tip } from '../interfaces/tip/Tip';
import './slider.css';

/* Translations */
import { tr } from '../../../utils/translations/translator';
import { useTranslation } from 'react-i18next';

/** Utils */
import { PriceType, PRICE_TYPES } from '../interfaces/utils';
import { Validity } from '../interfaces/tip/Validations';

import { valueof } from '../interfaces/tip/MatchForecast';
import { Price } from '../../PaymentGateway/interfaces/Price';

/* Assets */
import SVG from 'react-inlinesvg';
import dropdown from '../../../assets/dropdown.svg';
import Slider from '@material-ui/core/Slider';
import { withStyles } from '@material-ui/core/styles';

/* Redux */
import { RootState } from '../../../store';
import { connect } from 'react-redux';
import { LoginState } from '../../../store/login/reducer';
import { buildTierByCurrency } from './utils';
import { CurrencyCode } from '../../PaymentGateway/interfaces/currencies';
import { PricingTier } from '../../PaymentGateway/utils/pricingTier';
import { useFeatureFlagContext } from '../../__providers__/FeatureFlagsContext';

const PrettoSlider = withStyles({
  root: {
    padding: '45px 0px !important',
  },
  track: {
    color: '#e5e5e5',
    height: '3px !important',
    opacity: '1 !important',
  },
  rail: {
    color: '#e5e5e5',
    height: '3px !important',
    opacity: '1 !important',
  },
  thumb: {
    width: '24px !important',
    height: '24px !important',
    color: '#242424',
    marginTop: '-11px !important',
    boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.5)',
  },
  valueLabel: {
    color: 'transparent !important',
    left: 'unset !important',
    top: '-20px !important',
    fontSize: '14px !important',
    fontWeight: 500,
  },
})(Slider);
export interface TipTypeOptions {
  type: PriceType | null;
  code: string;
}
interface dispatchProps {
  loginState: LoginState;
}

interface TipTypeSelectionProps {
  pricingTiers: PricingTier[];
  hooks: {
    tip: Tip;
    handleTipValue: (key: keyof Tip, value: valueof<Tip>) => void;
  };
  tipValues: {
    priceType: PriceType | null;
    pricingTierCode: string;
  };
  onSelect: (tipTypeOptions: TipTypeOptions) => void;
  validity?: Validity | false;
  currency: CurrencyCode;
}

export const TipTypeSelection: React.FC<
  TipTypeSelectionProps & dispatchProps
> = ({
  pricingTiers,
  hooks: { tip, handleTipValue },
  tipValues: { priceType },
  onSelect,
  validity,
  currency,
  loginState,
}) => {
  const { t } = useTranslation();
  const cx = classNames.bind(styles);
  const isValid = validity ? validity.isValid : true;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { user } = loginState.user!;
  const { isEnabled } = useFeatureFlagContext();
  const isPriceFlagEnabled = isEnabled('tipster_price_in_usd_enabled');

  const tipPricingTier = pricingTiers.find(
    (tier) => tier.code === user.tips_pricing_tier,
  );

  const tipPrice = useMemo(
    () =>
      tipPricingTier && isPriceFlagEnabled
        ? new Price(
            tipPricingTier.web.default_price.amount,
            tipPricingTier.web.default_price.currency,
            tipPricingTier.web.default_price.decimals,
          )
        : buildTierByCurrency(tipPricingTier, currency),
    [tipPricingTier, isPriceFlagEnabled],
  );

  const buildFinalPrice: string = useMemo(() => {
    if (tip.discountPercentage) {
      const finalPrice = new Price(
        tipPrice.amount - (tipPrice.amount * tip.discountPercentage) / 100,
        tipPrice.currency,
        tipPrice.decimals,
      );

      return finalPrice.buildPrice().toString();
    }

    return tipPrice.buildPrice().toString();
  }, [tip.discountPercentage, tipPrice]);

  //TODO: delete after flag permanently enabled
  const isChannelsFlagEnabled = isEnabled('channel_based_tips_publishing');

  return (
    <div className={styles.TipTypeSelection}>
      <div className={styles.priceInputContainer}>
        <p className={styles.infoLabel}>
          {tr(t, 'priceInputs_info2').toUpperCase()}
        </p>
        {/* //FREE */}
        <label
          className={
            priceType === PRICE_TYPES.FREE
              ? styles.checkedCheckbox
              : isValid
              ? styles.uncheckedCheckbox
              : cx(styles.uncheckedCheckbox, styles.invalid)
          }
          id={styles.freeCheckbox}
          onClick={() => {
            onSelect({ type: PRICE_TYPES.FREE, code: '' });
          }}
        >
          <div>
            <div className={styles.priceTypeContainer}>
              <div
                className={
                  priceType === PRICE_TYPES.FREE ? styles.checked : undefined
                }
              />
            </div>
            <div className={styles.priceTypeText}>
              <strong>{tr(t, 'free_tip')}</strong>
              <label>{tr(t, 'priceInputs_free')}</label>
            </div>
          </div>
        </label>

        {tip.channel.type !== 'challenge' && 
        <label
          className={
            priceType === PRICE_TYPES.PAID
              ? cx(styles.checkedCheckbox, styles.tiersBox)
              : isValid
              ? cx(styles.uncheckedCheckbox, styles.tiersBox)
              : cx(styles.uncheckedCheckbox, styles.tiersBox, styles.invalid)
          }
          id={styles.paidCheckbox}
        >
          <strong>{tr(t, PRICE_TYPES.PAID)}</strong>
          {/* //PREMIUM */}
          {isChannelsFlagEnabled &&
            tip.channel.publishable_tip_types?.includes('PREMIUM') && (
              <div
                className={styles.tiersContainer}
                onClick={() => {
                  onSelect({ type: PRICE_TYPES.PREMIUM, code: '' });
                }}
              >
                <div>
                  <div className={styles.priceTypeContainer}>
                    <div
                      className={
                        priceType === PRICE_TYPES.PREMIUM
                          ? styles.checked
                          : undefined
                      }
                    />
                  </div>
                  <div className={styles.priceTypeText}>
                    <strong>{tr(t, 'premium_tip')}</strong>
                    <label>{tr(t, 'premiumPriceType-info')}</label>
                  </div>
                </div>
              </div>
            )}

          {/* //SAFE */}
          <div
            className={styles.tiersContainer}
            id={styles.safePriceContainer}
            onClick={() => {
              onSelect({ type: PRICE_TYPES.PAID, code: '' });
            }}
          >
            <div>
              <div className={styles.priceTypeContainer}>
                <div
                  className={
                    priceType === PRICE_TYPES.PAID ? styles.checked : undefined
                  }
                />
              </div>
              <div className={styles.priceTypeText}>
                <strong>
                  {tr(t, 'SAFE')}
                  <span>{tipPrice.buildPrice().toString()}*</span>
                </strong>
                <label>{tr(t, 'safePriceType-info')}</label>
                <label>{tr(t, 'safePriceType-info2')}</label>
                <div className={styles.discountWrapper}>
                  <strong onClick={() => setIsDropdownOpen(true)}>
                    {tr(t, 'apply-discount')}

                    <SVG
                      className={styles.dropdown}
                      id={isDropdownOpen ? styles.showMore : styles.showLess}
                      src={dropdown}
                    />
                  </strong>
                  <div className={styles.dropdownContent}>
                    <div
                      className={
                        isDropdownOpen ? styles.expanded : styles.collapsed
                      }
                    >
                      <div className={styles.sliderWrapper}>
                        <PrettoSlider
                          aria-label="Always visible"
                          value={tip.discountPercentage}
                          valueLabelDisplay="on"
                          min={0}
                          max={90}
                          onChange={(
                            event: any,
                            newValue: number | number[],
                          ) => {
                            handleTipValue(
                              'discountPercentage',
                              newValue as number,
                            );
                          }}
                        />
                      </div>
                      <label>
                        {tr(t, 'final_price')}
                        <strong>{buildFinalPrice}</strong>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </label>
        }
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  loginState: state.login,
});

export const TipTypeSelectionConnected =
  connect(mapStateToProps)(TipTypeSelection);
